/**
 * Spec 027, PR 4: the process scheme drawn from its data, and the form that edits a step (D3).
 * The scheme is layers of steps with arrows and a badge for who does each — the staff member
 * itself or one of its helpers (Spec 042: helpers are cards of their own, added on the
 * «Помощники» tab); tapping a step opens its form. Nothing is dragged: every save is a new
 * version of the card, which each person approves for their own computer (D4).
 */

import { useId, useState } from "react";

import type { AgentDetailOut, AgentSchemeStep } from "@/api/types";
import { Tick, useAgentSave } from "@/components/AgentForm";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { Caption } from "@/kit";
import { dependentsOf, ORCHESTRATOR, schemeEdges, schemeLayers, stepKey, whoLabel } from "@/lib/agents";

const W = 164;
const H = 68;
const GAP_X = 14;
const GAP_Y = 36;

/** The scheme as the app draws it: a step sits one layer below the last step it waits for. */
export function SchemeView({ scheme, onOpen }: { scheme: AgentSchemeStep[]; onOpen?: (step: AgentSchemeStep) => void }) {
  const marker = useId().replace(/:/g, "");
  const layers = schemeLayers(scheme);
  const width = Math.max(...layers.map((layer) => layer.length)) * (W + GAP_X) - GAP_X;
  const height = layers.length * (H + GAP_Y) - GAP_Y;
  const at = new Map<string, { x: number; y: number }>();
  layers.forEach((layer, row) => {
    const left = (width - (layer.length * (W + GAP_X) - GAP_X)) / 2;
    layer.forEach((step, col) => at.set(step.key, { x: left + col * (W + GAP_X), y: row * (H + GAP_Y) }));
  });
  return (
    <div className="overflow-x-auto pb-1" role="group" aria-label="Схема процесса">
      <div className="relative mx-auto" style={{ width, height }}>
        <svg className="absolute inset-0 text-p-text-muted" width={width} height={height} aria-hidden="true">
          <defs>
            <marker id={marker} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
            </marker>
          </defs>
          {schemeEdges(scheme).map(([from, to]) => {
            const a = at.get(from)!;
            const b = at.get(to)!;
            const x1 = a.x + W / 2;
            const y1 = a.y + H;
            const x2 = b.x + W / 2;
            const y2 = b.y - 2;
            const mid = (y1 + y2) / 2;
            return (
              <path key={`${from}>${to}`} d={`M${x1},${y1} C${x1},${mid} ${x2},${mid} ${x2},${y2}`}
                fill="none" stroke="currentColor" strokeWidth="1.5" markerEnd={`url(#${marker})`} />
            );
          })}
        </svg>
        {scheme.map((step) => {
          const p = at.get(step.key)!;
          return (
            <button key={step.key} type="button" disabled={!onOpen} onClick={() => onOpen?.(step)}
              aria-label={`${step.title}, делает ${whoLabel(step.who)}`}
              className="absolute flex flex-col justify-between overflow-hidden rounded-card border border-p-border bg-p-surface px-3 py-2 text-left press-surface"
              style={{ left: p.x, top: p.y, width: W, height: H }}>
              <span className="line-clamp-2 text-meta font-semibold leading-4 text-p-text">{step.title}</span>
              <span className="truncate text-micro text-p-text-muted">{whoLabel(step.who)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** One step of the scheme (D3): who does it, what it waits for, what goes in and out, the check. */
export function StepSheet({ agent, step, onClose }: { agent: AgentDetailOut; step: AgentSchemeStep | null; onClose: () => void }) {
  const scheme = agent.card.scheme;
  const [title, setTitle] = useState(step?.title ?? "");
  const [who, setWho] = useState(step?.who ?? ORCHESTRATOR);
  const [after, setAfter] = useState<string[]>(step?.after ?? []);
  const [input, setInput] = useState(step?.input_md ?? "");
  const [output, setOutput] = useState(step?.output_md ?? "");
  const [check, setCheck] = useState(step?.check_md ?? "");
  const [removing, setRemoving] = useState(false);
  const save = useAgentSave(agent, onClose, step ? "Шаг сохранён" : "Шаг добавлен");
  // Spec 042: the doers — the card's own chat, and a staff member's live helpers by name.
  const helpers = (agent.helpers ?? []).filter((h) => !h.archived_at);

  // A step never waits for itself or for the steps that wait for it: the server refuses a loop.
  const loops = step ? dependentsOf(scheme, step.key) : new Set<string>();
  const others = scheme.filter((s) => s.key !== step?.key);
  const next: AgentSchemeStep = {
    key: step?.key ?? stepKey(title, scheme.map((s) => s.key)),
    title: title.trim(),
    who,
    after: after.filter((key) => others.some((s) => s.key === key)),
    input_md: input,
    output_md: output,
    check_md: check,
  };
  const changed = !step || JSON.stringify(next) !== JSON.stringify(step);
  const saved = step ? scheme.map((s) => (s.key === step.key ? next : s)) : [...scheme, next];
  const without = scheme.filter((s) => s.key !== step?.key).map((s) => ({ ...s, after: s.after.filter((key) => key !== step?.key) }));
  const toggle = (key: string) => setAfter(after.includes(key) ? after.filter((k) => k !== key) : [...after, key]);

  return (
    <Sheet open onClose={onClose} title={step ? `Шаг «${step.title}»` : "Новый шаг"}
      footer={
        <div className="flex flex-col gap-2">
          <Button block variant="strong" loading={save.isPending && !removing} disabled={!title.trim() || !changed}
            onClick={() => save.mutate({ scheme: saved })}>
            {step ? "Сохранить шаг" : "Добавить шаг"}
          </Button>
          {step && (
            <Button block variant="ghost" loading={save.isPending && removing}
              onClick={() => (removing ? save.mutate({ scheme: without }) : setRemoving(true))}>
              {removing ? "Точно убрать шаг" : "Убрать шаг"}
            </Button>
          )}
        </div>
      }>
      <div className="flex flex-col gap-3">
        <TextField label="Что делает шаг" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        <label className="flex flex-col gap-1 text-meta text-p-text-muted">
          Кто делает
          <select value={who} onChange={(e) => setWho(e.target.value)}
            className="min-h-11 rounded-lg border border-p-border-strong bg-p-surface px-3 text-body text-p-text">
            <option value={ORCHESTRATOR}>{agent.parent_id ? "помощник сам" : "сотрудник сам"}</option>
            {helpers.map((h) => (
              <option key={h.id} value={h.name}>{h.name}{h.helper_mode === "own_chat" ? " — в своём чате" : ""}</option>
            ))}
          </select>
        </label>
        {!agent.parent_id && !helpers.length && (
          <p className="text-meta text-p-text-muted">Помощников пока нет: их добавляют на вкладке «Помощники», и тогда шаг можно отдать одному из них.</p>
        )}
        {others.length > 0 && (
          <section className="space-y-1">
            <Caption>Ждёт шаги</Caption>
            {others.map((s) => (
              <Tick key={s.key} label={s.title} note={loops.has(s.key) ? "ждёт этот шаг — был бы круг" : whoLabel(s.who)}
                on={after.includes(s.key)} disabled={loops.has(s.key)} onChange={() => toggle(s.key)} />
            ))}
          </section>
        )}
        <TextArea label="На входе" value={input} rows={2} maxLength={4000} onChange={(e) => setInput(e.target.value)} />
        <TextArea label="На выходе" value={output} rows={2} maxLength={4000} onChange={(e) => setOutput(e.target.value)} />
        <TextArea label="Как проверить" hint="Сотрудник сверяет результат с этим и возвращает шаг, если не сходится"
          value={check} rows={2} maxLength={4000} onChange={(e) => setCheck(e.target.value)} />
        <p className="text-meta text-p-text-muted">Изменение — новая версия карточки: её одобряет каждый для своего компьютера.</p>
      </div>
    </Sheet>
  );
}
